<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useMapStore } from '@/stores/map';
import { useEditorStore } from '@/stores/editor';
import { TileData, TILE_SIZE, isWaterTile } from '@/formats/types';

const props = defineProps<{
    floating?: boolean;
}>();

const emit = defineEmits<{
    (e: 'toggle-float'): void;
}>();

const mapStore = useMapStore();
const editorStore = useEditorStore();

const canvasRef = ref<HTMLCanvasElement | null>(null);
const minimapSize = 200;

// The map at one pixel per tile. Edits repaint single pixels here, and the
// visible canvas is just this image scaled to fit.
const tileCanvas = document.createElement('canvas');

// Compute scale factor (minimap pixels per tile)
const scale = computed(() => {
    if (!mapStore.hasMap) return 1;
    const maxDim = Math.max(mapStore.mapWidth, mapStore.mapHeight);
    return minimapSize / maxDim;
});

// Minimap color for a tile, as [r, g, b]
function getTileColor(tile: TileData): [number, number, number] {
    if (tile.teleport) return [0x9b, 0x59, 0xb6];
    if (tile.blocked) return [0xff, 0x44, 0x44];

    // Object overlay
    if (tile.objectSprite >= 100 && tile.objectSprite < 200) return [0x22, 0x8b, 0x22]; // Trees
    if (tile.objectSprite > 0) return [0x8b, 0x45, 0x13]; // Objects

    // Base tile color (simplified)
    const sprite = tile.tileSprite;
    if (isWaterTile(tile) || sprite === 366 || sprite === 367) return [0x34, 0x98, 0xdb]; // Water
    if (sprite === 0 && tile.tileSpriteFrame === 0) return [0x10, 0x10, 0x18]; // Empty
    if (sprite >= 50 && sprite < 70) return [0x8b, 0x45, 0x13]; // Structures
    if (sprite >= 70 && sprite < 100) return [0x69, 0x69, 0x69]; // Interior
    if (sprite >= 300 && sprite < 320) return [0x50, 0x50, 0x50]; // Stone
    if (sprite >= 320 && sprite < 330) return [0xe8, 0xe8, 0xe8]; // Snow
    if (sprite >= 330 && sprite < 349) return [0xc4, 0xa3, 0x5a]; // Sand
    return [0x2d, 0x5a, 0x27]; // Default grass green
}

// Repaint every tile (a new map was opened)
function drawAllTiles(): void {
    const doc = mapStore.document;
    const ctx = tileCanvas.getContext('2d');
    if (!doc || !ctx) return;

    const { width, height, tiles } = doc;
    tileCanvas.width = width;
    tileCanvas.height = height;

    const image = ctx.createImageData(width, height);
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const [r, g, b] = getTileColor(tiles[x][y]);
            const offset = (y * width + x) * 4;
            image.data[offset] = r;
            image.data[offset + 1] = g;
            image.data[offset + 2] = b;
            image.data[offset + 3] = 255;
        }
    }
    ctx.putImageData(image, 0, 0);

    scheduleRedraw();
}

// Repaint one tile (it was edited)
function handleTileChanged(x: number, y: number, tile: TileData): void {
    const ctx = tileCanvas.getContext('2d');
    if (!ctx) return;

    const [r, g, b] = getTileColor(tile);
    ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
    ctx.fillRect(x, y, 1, 1);

    scheduleRedraw();
}

// Scale the tile image onto the visible canvas
function drawMinimap(): void {
    const ctx = canvasRef.value?.getContext('2d');
    if (!ctx) return;

    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, minimapSize, minimapSize);

    if (!mapStore.document || tileCanvas.width === 0) return;

    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(
        tileCanvas,
        0, 0,
        mapStore.mapWidth * scale.value,
        mapStore.mapHeight * scale.value
    );
}

// Navigate by clicking or dragging on the minimap
const isNavigating = ref(false);

function navigateTo(event: PointerEvent): void {
    if (!canvasRef.value || !mapStore.hasMap) return;

    const rect = canvasRef.value.getBoundingClientRect();
    const tileX = Math.floor((event.clientX - rect.left) / scale.value);
    const tileY = Math.floor((event.clientY - rect.top) / scale.value);

    editorStore.centerOnTile(
        Math.max(0, Math.min(mapStore.mapWidth - 1, tileX)),
        Math.max(0, Math.min(mapStore.mapHeight - 1, tileY)),
        TILE_SIZE
    );
}

function handlePointerDown(event: PointerEvent): void {
    isNavigating.value = true;
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    navigateTo(event);
}

function handlePointerMove(event: PointerEvent): void {
    if (isNavigating.value) {
        navigateTo(event);
    }
}

function handlePointerUp(): void {
    isNavigating.value = false;
}

// Separate canvas for cursor overlay (avoids redrawing entire map)
const cursorCanvasRef = ref<HTMLCanvasElement | null>(null);

// Draw just the cursor overlay (cheap - called on mouse move)
function drawCursor(): void {
    if (!cursorCanvasRef.value) return;

    const canvas = cursorCanvasRef.value;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const s = scale.value;

    // Clear cursor layer
    ctx.clearRect(0, 0, minimapSize, minimapSize);
    if (!mapStore.document) return;

    // Draw viewport rectangle
    if (editorStore.zoom > 0 && editorStore.viewportWidth > 0) {
        const tilePixels = TILE_SIZE * editorStore.zoom;
        const viewWidth = editorStore.viewportWidth / tilePixels;
        const viewHeight = editorStore.viewportHeight / tilePixels;
        const viewX = -editorStore.panX / tilePixels;
        const viewY = -editorStore.panY / tilePixels;

        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.strokeRect(viewX * s, viewY * s, viewWidth * s, viewHeight * s);
    }

    // Draw cursor position
    if (editorStore.tileX >= 0 && editorStore.tileY >= 0) {
        ctx.fillStyle = '#ffff00';
        ctx.fillRect(
            Math.floor(editorStore.tileX * s),
            Math.floor(editorStore.tileY * s),
            Math.max(2, Math.ceil(s)),
            Math.max(2, Math.ceil(s))
        );
    }
}

// Both layers redraw at most once per frame
let redrawScheduled = false;
function scheduleRedraw(): void {
    if (redrawScheduled) return;
    redrawScheduled = true;
    requestAnimationFrame(() => {
        redrawScheduled = false;
        drawMinimap();
    });
}

let cursorUpdateScheduled = false;
function scheduleCursorUpdate(): void {
    if (cursorUpdateScheduled) return;
    cursorUpdateScheduled = true;
    requestAnimationFrame(() => {
        cursorUpdateScheduled = false;
        drawCursor();
    });
}

// The document ref is only replaced when a different map is opened
watch(() => mapStore.document, () => {
    drawAllTiles();
    scheduleCursorUpdate();
});

// Watch for cursor/viewport changes - throttled updates
watch(
    [
        () => editorStore.tileX,
        () => editorStore.tileY,
        () => editorStore.zoom,
        () => editorStore.panX,
        () => editorStore.panY,
        () => editorStore.viewportWidth,
        () => editorStore.viewportHeight,
    ],
    scheduleCursorUpdate
);

let unsubscribeTileChanges: (() => void) | null = null;

onMounted(() => {
    unsubscribeTileChanges = mapStore.onTileChanged(handleTileChanged);
    drawAllTiles();
    drawCursor();
});

onUnmounted(() => {
    unsubscribeTileChanges?.();
});
</script>

<template>
    <div class="minimap">
        <div class="panel-header">
            <h3>Minimap</h3>
            <button
                class="float-toggle-btn"
                @click="emit('toggle-float')"
                :title="props.floating ? 'Dock to sidebar' : 'Pop out'"
            >
                {{ props.floating ? '⇲' : '⇱' }}
            </button>
        </div>
        <div class="minimap-container">
            <div class="minimap-canvas-wrapper">
                <canvas
                    ref="canvasRef"
                    class="minimap-base"
                    :width="minimapSize"
                    :height="minimapSize"
                    @pointerdown="handlePointerDown"
                    @pointermove="handlePointerMove"
                    @pointerup="handlePointerUp"
                    @pointercancel="handlePointerUp"
                />
                <canvas
                    ref="cursorCanvasRef"
                    class="minimap-cursor"
                    :width="minimapSize"
                    :height="minimapSize"
                />
            </div>
        </div>
        <div class="minimap-info" v-if="mapStore.hasMap">
            <span>{{ mapStore.mapWidth }} x {{ mapStore.mapHeight }}</span>
        </div>
    </div>
</template>

<style scoped>
.minimap {
    overflow: hidden;
    flex-shrink: 0;
    border-top: 1px solid var(--border-color);
}

.panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    background: rgba(0, 0, 0, 0.2);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.panel-header h3 {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-secondary);
}

.float-toggle-btn {
    width: 22px;
    height: 22px;
    padding: 0;
    background: var(--bg-tertiary);
    border: 1px solid var(--border-color);
    border-radius: 4px;
    color: var(--text-secondary);
    font-size: 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s ease;
}

.float-toggle-btn:hover {
    background: var(--accent-color);
    border-color: var(--accent-color);
    color: white;
}

.minimap-container {
    padding: 8px;
    display: flex;
    justify-content: center;
}

.minimap-canvas-wrapper {
    position: relative;
    width: 200px;
    height: 200px;
}

.minimap-base,
.minimap-cursor {
    position: absolute;
    top: 0;
    left: 0;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    cursor: crosshair;
    image-rendering: pixelated;
}

.minimap-cursor {
    pointer-events: none;
}

.minimap-info {
    padding: 4px 12px 8px;
    text-align: center;
    font-size: 11px;
    color: var(--text-secondary);
}
</style>
